import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { CaretDown, CaretRight, CheckCircle, Warning, MagnifyingGlass } from "@phosphor-icons/react";
import ClaimsPage from "./ClaimsPage";
import InventoryPage from "./InventoryPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_LABEL = { clinical: "Clinical", economic: "Economic", rwe: "RWE", pro_qol: "PRO/QoL", epidemiology: "Epidemiology", hta: "HTA", regulatory: "Regulatory", pricing: "Pricing", other: "Other" };

const SummaryStrip = ({ s }) => {
  const navigate = useNavigate();
  const LINKS = { needs_review: "/console/evidence-review?tab=sources", citation_issues: "/console/evidence-review?tab=sources", conflicts: "/console/evidence-review?tab=conflicts" };
  return (
    <div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="library-summary">
        {[["total", "Total Evidence Assets", "#0F172A"], ["processed", "Processed", "#1D4ED8"],
          ["verified", "Verified", "#047857"], ["needs_review", "Needs Review", "#B45309"],
          ["citation_issues", "Citation Issues", "#B91C1C"], ["conflicts", "Conflicts", "#7C2D12"]].map(([k, l, c]) => (
          <button key={k} data-testid={`library-stat-${k}`} disabled={!LINKS[k] || !s?.[k]}
            onClick={() => LINKS[k] && navigate(LINKS[k])}
            className={`bg-white border border-slate-200 px-4 py-3 text-left ${LINKS[k] && s?.[k] ? "hover:border-slate-400 cursor-pointer" : "cursor-default"}`}>
            <p className="font-mono-data text-2xl font-bold" style={{ color: c }}>{s?.[k] ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{l}</p>
            {LINKS[k] && s?.[k] > 0 && <p className="text-[9px] text-slate-400 mt-0.5">Resolve in Evidence Review →</p>}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-2" data-testid="library-pipeline-note">
        How evidence gets here: approving facts in the <Link to="/console/knowledge" className="underline">Knowledge Hub</Link> adds
        records to this library. <b>Verified</b> is a stricter governance bar — it requires a complete citation, a validated original
        source and reviewer approval, handled in <Link to="/console/evidence-review" className="underline">Evidence Review</Link>.
        Approved ≠ verified by design: nothing becomes verified silently.
      </p>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">{label}</p>
    <p className="text-[11px] text-slate-700">{children ?? "—"}</p></div>
);

const Record = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`library-record-${r.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50">
        {open ? <CaretDown size={12} className="text-slate-400 shrink-0" /> : <CaretRight size={12} className="text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-800 truncate">{r.name}</p>
          <p className="text-[10px] text-slate-400">{(r.type || "").replaceAll("_", " ")} · {DOMAIN_LABEL[r.domain]} · {r.scope}{r.maturity ? ` · ${r.maturity.replaceAll("_", " ")}` : ""}</p>
        </div>
        {r.evidence_grade && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-slate-100 text-slate-600">Grade {r.evidence_grade}</span>}
        {r.source_verified
          ? <span className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase bg-emerald-50 text-emerald-700"><CheckCircle size={10} /> Verified</span>
          : <span className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase bg-amber-50 text-amber-700"><Warning size={10} /> Needs review</span>}
      </button>
      {open && (
        <div className="px-9 pb-4 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2" data-testid={`library-detail-${r.id}`}>
          <div className="col-span-2 md:col-span-4"><Field label="Description">{r.description}</Field></div>
          <Field label="Evidence type">{(r.type || "").replaceAll("_", " ")}</Field>
          <Field label="Domain">{DOMAIN_LABEL[r.domain]}</Field>
          <Field label="Countries">{(r.localized_for || []).map((m) => m.replaceAll("_", " ")).join(", ") || (r.country ? r.country.replaceAll("_", " ") : r.scope === "global" ? "Global" : "—")}</Field>
          <Field label="Indication / population">{r.indication || r.population || "—"}</Field>
          <Field label="Comparator">{r.comparator || "—"}</Field>
          <Field label="Endpoints">{Array.isArray(r.endpoints) ? r.endpoints.join(", ") : r.endpoints || (r.themes || []).join(", ") || "—"}</Field>
          <Field label="Quality / confidence">{r.design_label || r.source_confidence || "—"}{r.quality_reviewed ? " · quality reviewed" : ""}</Field>
          <Field label="Verification">{r.source_verified ? "Source verified" : "Not verified"}{r.citation_complete ? " · citation complete" : ""}{r.fact_approval_unknown ? " · approval unknown" : ""}</Field>
          <div className="col-span-2 md:col-span-4">
            <Field label="Provenance">
              {[r.source_document && `From your uploaded document: ${r.source_document}`,
                r.source_document_url && `link: ${r.source_document_url}`,
                r.source, r.year, r.internal_analysis && "internal analysis", r.generated_by_action && `generated by action ${r.generated_by_action}`,
                r.provenance?.document].filter(Boolean).join(" · ") || "—"}
            </Field>
          </div>
          {r.claims?.length > 0 && (
            <div className="col-span-2 md:col-span-4">
              <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">Extracted claims ({r.claims.length})</p>
              {r.claims.map((c) => <p key={c.id} className="text-[11px] text-slate-700 border-l-2 border-slate-200 pl-2 mb-1">{c.statement} <span className="text-slate-400">({c.category})</span></p>)}
            </div>
          )}
          {r.citations?.length > 0 && (
            <div className="col-span-2 md:col-span-4">
              <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">Associated references ({r.citations.length})</p>
              {r.citations.map((c) => <p key={c.id} className="text-[11px] text-slate-600">{c.filename || c.id} — <span className={c.status === "verified" ? "text-emerald-700" : "text-amber-700"}>{c.status_label || c.status}</span></p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function EvidenceLibraryPage() {
  const { productId, product } = useProduct();
  const [params, setParams] = useSearchParams();
  const view = params.get("tab") || "records";
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    setData(null);
    axios.get(`${API}/evidence-experience/${productId}/library`).then((r) => setData(r.data)).catch(console.error);
  }, [productId]);

  const records = (data?.records || []).filter((r) =>
    (!typeFilter || r.type === typeFilter) &&
    (!q || `${r.name} ${r.description} ${r.type}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <PageShell pageId="evidence-library" overline={`Evidence · ${product?.name || ""}`} title="Evidence Library">
      <div className="flex gap-1" data-testid="library-views">
        {[["records", "Evidence Records"], ["claims", "Claims & References"], ["advanced", "Inventory Engine (Advanced)"]].map(([v, l]) => (
          <button key={v} data-testid={`library-view-${v}`} onClick={() => setParams(v === "records" ? {} : { tab: v })}
            className={`text-[11px] font-semibold px-3 py-1.5 ${view === v ? "bg-slate-900 text-white" : "text-slate-500 border border-slate-200 hover:bg-slate-50 bg-white"}`}>{l}</button>
        ))}
      </div>
      {view === "claims" && <ClaimsPage embedded />}
      {view === "advanced" && <InventoryPage embedded />}
      {view === "records" && (
        <>
          <SummaryStrip s={data?.summary} />
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search evidence…"
                className="border border-slate-200 bg-white pl-8 pr-3 py-2 text-xs w-64 focus:outline-none focus:border-slate-400" />
            </div>
            <button data-testid="filter-all" onClick={() => setTypeFilter("")}
              className={`text-[10px] font-semibold px-2.5 py-1.5 ${!typeFilter ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>
              All ({data?.summary?.total ?? 0})</button>
            {Object.entries(data?.type_counts || {}).map(([t, n]) => (
              <button key={t} data-testid={`filter-${t}`} onClick={() => setTypeFilter(typeFilter === t ? "" : t)}
                className={`text-[10px] font-semibold px-2.5 py-1.5 capitalize ${typeFilter === t ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                {t.replaceAll("_", " ")} ({n})</button>
            ))}
          </div>
          <div className="bg-white border border-slate-200" data-testid="library-records">
            {!data ? <p className="px-4 py-10 text-center text-xs text-slate-400">Loading…</p>
              : records.length === 0 ? <p className="px-4 py-10 text-center text-xs text-slate-400">No evidence matches the current filter.</p>
              : records.map((r) => <Record key={r.id} r={r} />)}
          </div>
          <p className="text-[11px] text-slate-400">
            Every record stays connected to its Knowledge Hub source and provenance. Exceptions are handled in{" "}
            <Link to="/console/evidence-review" className="underline font-semibold text-slate-600">Evidence Review</Link>.
          </p>
        </>
      )}
    </PageShell>
  );
}
